import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon, { type IconName } from '../../../components/AppIcon';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { cn } from '@/utils/cn';

export type KPIMetric = {
  id: string;
  title: string;
  value: string;
  /** Only present when a real prior-period comparison is available */
  change?: string;
  /** Only meaningful when `change` is present */
  trend?: 'up' | 'down';
  /** When true, render the trend chip in a neutral tone (e.g. "+5 net") */
  isNeutralChange?: boolean;
  icon: string;
  color: 'blue' | 'green' | 'orange' | 'purple' | 'red';
  category: 'attendance' | 'payroll' | 'employee' | 'project';
  description: string;
};

export type KPICardProps = {
  metric: KPIMetric;
  isPrimary?: boolean;
};

const KPICard: React.FC<KPICardProps> = ({ metric, isPrimary = false }) => {
  const { t } = useTranslation();
  const [showDescription, setShowDescription] = useState(false);

  const getIconStyles = (color: KPIMetric['color']) => {
    switch (color) {
      case 'blue':
        return 'p-2 bg-primary/10 rounded-lg text-primary';
      case 'green':
        return 'p-2 bg-success/10 rounded-lg text-success';
      case 'orange':
        return 'p-2 bg-warning/10 rounded-lg text-warning';
      case 'purple':
        return 'p-2 bg-purple-100 rounded-lg text-purple-600';
      case 'red':
        return 'p-2 bg-error/10 rounded-lg text-error';
      default:
        return 'p-2 bg-muted rounded-lg text-muted-foreground';
    }
  };

  const hasRealChange = metric?.change != null;

  const renderTrendChip = () => {
    if (!hasRealChange) {
      return (
        <span className="inline-flex items-center text-[11px] text-muted-foreground/70">
          <Minus className="w-3 h-3 me-1" />
          {t('No comparison')}
        </span>
      );
    }
    if (metric?.isNeutralChange) {
      return (
        <span className="inline-flex items-center text-xs font-medium text-muted-foreground tabular-nums">
          {metric.change}
        </span>
      );
    }
    const isUp = metric?.trend === 'up';
    return (
      <span className={cn(
        'inline-flex items-center text-xs font-medium tabular-nums',
        isUp ? 'text-success' : 'text-error',
      )}>
        {isUp ? <TrendingUp className="w-3 h-3 me-1" /> : <TrendingDown className="w-3 h-3 me-1" />}
        {metric.change}
      </span>
    );
  };

  return (
    <Card
      variant="elevated"
      className={cn(
        'relative hover:shadow-md transition-all focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none',
        isPrimary ? 'p-6' : 'p-4',
      )}
      tabIndex={0}
      onMouseEnter={() => setShowDescription(true)}
      onMouseLeave={() => setShowDescription(false)}
      onFocus={() => setShowDescription(true)}
      onBlur={() => setShowDescription(false)}
      data-testid={`kpi-card-${metric?.id || 'unknown'}`}
      data-kpi-category={metric?.category}
      data-kpi-primary={isPrimary}
    >
      <div className="flex items-start gap-3 relative z-10">
        <div className={getIconStyles(metric?.color)}>
          <Icon name={metric?.icon as IconName} size={isPrimary ? 24 : 20} />
        </div>
        <div className="flex-1 min-w-0">
          <h4
            className={cn(
              'font-medium text-muted-foreground mb-1 truncate',
              isPrimary ? 'text-sm' : 'text-xs',
            )}
            data-testid="kpi-title"
          >
            {metric?.title}
          </h4>
          <p
            className={cn(
              'font-bold text-foreground tabular-nums',
              isPrimary ? 'text-2xl' : 'text-xl',
            )}
            data-testid="kpi-value"
          >
            {metric?.value}
          </p>
          <div className="flex items-center justify-between mt-2 gap-2">
            {renderTrendChip()}
            {hasRealChange && !metric?.isNeutralChange && (
              <span className="text-[11px] text-muted-foreground truncate">{t('vs_previous')}</span>
            )}
          </div>
        </div>
      </div>

      {/* Hover description tooltip */}
      {showDescription && (
        <div className="absolute inset-x-0 top-full mt-2 z-20 bg-foreground text-background text-xs rounded-lg p-3 shadow-xl max-w-sm pointer-events-none">
          <div className="absolute -top-1 start-4 w-2 h-2 bg-foreground rotate-45"></div>
          {metric?.description}
        </div>
      )}
    </Card>
  );
};

export default KPICard;
